// ConcessionOverviewReportGenerator.jsx
import React, { useState } from 'react';
import { generateTitlePage } from './TitlePage.js';
import { generateSectionChartsPages } from './SectionChartPages.js';
import { generateExecutiveSummaryPage } from './ExecutiveSummaryPage.js';
import { generateGamesComparedPage } from './GamesComparedPage.js';
import { generateSectionsByGamePage } from './SectionsByGamePage.js';
import { loadLibraries, loadLogoImage, loadTeamLogo, addPageFooters } from './assetLoaders';
import Icon from '../../../../components/atoms/Icon';
import '../../concession.css';
// Previous pages (Summary, Section Analysis, Revenue Opportunity) are no longer included; their files remain for reference.


const ConcessionOverviewReportGenerator = ({ 
  overviewState,
  summaryMetrics,
  gamesWithNoData,
  timeRange,
  currentLocation,
  isCountMode,
  formatTransactionTime,
  filteredChartData,
  authCtx,
  gameCards = [],
  insights = [],
  sections = [],
  pendingCount = 0
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');

  const generateReport = async () => {
    if (overviewState.selectedDates.length === 0) {
      alert('Please select at least one game to generate a report.');
      return;
    }

    setIsGenerating(true);
    setProgress(0);

    try {
      await loadLibraries(setProgressMessage);
      setProgress(10);

      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF('p', 'mm', 'a4');

      // Cover
      setProgressMessage('Creating cover page...');
      await generateTitlePage(pdf, {
        authCtx,
        currentLocation,
        overviewState,
        timeRange,
        gamesWithNoData,
        loadTeamLogo,
        loadLogoImage,
        gameCards
      });
      setProgress(20);

      // Executive summary: headline numbers + what stands out
      setProgressMessage('Writing executive summary...');
      generateExecutiveSummaryPage(pdf, {
        summaryMetrics,
        isCountMode,
        formatTransactionTime,
        gameCards,
        insights,
        timeRange
      });
      setProgress(30);

      // Games compared
      setProgressMessage('Comparing games...');
      generateGamesComparedPage(pdf, { gameCards, isCountMode, formatTransactionTime });
      setProgress(38);

      // Sections by game
      setProgressMessage('Comparing sections...');
      generateSectionsByGamePage(pdf, { gameCards, sections, isCountMode });
      setProgress(45);

      // One page per section: per-game stats + its charts as shown on the page
      setProgressMessage('Capturing section charts...');
      await generateSectionChartsPages(pdf, {
        setProgressMessage,
        setProgress,
        gameCards,
        isCountMode,
        formatTransactionTime
      });
      setProgress(90);

      // Add footers to all pages
      setProgressMessage('Finalizing report...');
      const reportDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      addPageFooters(pdf, currentLocation.name, reportDate);
      setProgress(95);

      // Save the PDF
      const filename = `${currentLocation.name.replace(/\s+/g, '_')}_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);

      setProgress(100);
      setProgressMessage('Report generated successfully!');
      setTimeout(() => {
        setIsGenerating(false);
        setProgress(0);
        setProgressMessage('');
      }, 1500);

    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report. Please try again.');
      setIsGenerating(false);
      setProgress(0);
      setProgressMessage('');
    }
  };

  // Wait until every selected game has loaded so the report never mixes in missing data
  const stillLoading = !!overviewState.loading || !!pendingCount;
  const disabled = isGenerating || overviewState.selectedDates.length === 0 || stillLoading;

  return (
    <>
      <button
        type="button"
        onClick={generateReport}
        disabled={disabled}
        className="cx-report-btn"
        title={stillLoading ? 'Loading game data…' : undefined}
      >
        {isGenerating ? (
          <>
            <span className="cx-report-spinner" aria-hidden="true" />
            <span>Generating…</span>
          </>
        ) : (
          <>
            <Icon name="file-earmark-arrow-down" />
            <span>Download report</span>
          </>
        )}
      </button>
      {isGenerating && (
        <div className="cx-report-overlay" role="dialog" aria-live="polite" aria-label="Generating report">
          <div className="cx-report-modal">
            <div className="cx-report-modal-icon">
              <Icon name="file-earmark-bar-graph" />
            </div>
            <h3 className="cx-report-modal-title">Building your report</h3>
            <p className="cx-report-modal-msg">{progressMessage || 'Preparing...'}</p>
            <div className="cx-report-progress">
              <div className="cx-report-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <div className="cx-report-modal-pct">{progress}%</div>
            <p className="cx-report-modal-note">Please keep this tab open. The page steps through each section to capture its charts.</p>
          </div>
        </div>
      )}
    </>
  );
};

export default ConcessionOverviewReportGenerator;